import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Avatar,
  Popper,
  Paper,
  Fade,
  IconButton,
} from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  Send as SendIcon,
  Lock as LockIcon,
  Link as LinkIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ContentCopy as ContentCopyIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { Tournament, Player } from "../types";
import { apiRequest } from "../config/api";
import { useAuth } from "../contexts/AuthContext";
import { useOrganization } from "../contexts/OrganizationContext";
import PasswordTextField from "./PasswordTextField";

interface TournamentInvitationDialogProps {
  open: boolean;
  onClose: () => void;
  tournament: Tournament;
  currentPlayer: Player;
  onSuccess?: () => void;
}

export default function TournamentInvitationDialog({
  open,
  onClose,
  tournament,
  currentPlayer,
  onSuccess,
}: TournamentInvitationDialogProps) {
  const { playerToken } = useAuth();
  const { currentOrg } = useOrganization();
  const [registrationPath, setRegistrationPath] = useState<
    "invitation" | "partner-board" | null
  >(null);
  const [preferredInvitationTab, setPreferredInvitationTab] = useState<0 | 1 | 2>(0);

  // Tab selection for "I have a partner" path
  const [partnerTab, setPartnerTab] = useState<0 | 1 | 2>(0); // 0: Existing Player, 1: New Player, 2: Board

  // First-time step-by-step guide for the partner tabs
  const [guideStep, setGuideStep] = useState<number | null>(null);
  const isPadelEagle = currentOrg?.slug === "padel-eagle";
  const primaryColor = isPadelEagle ? "#743444" : "#1976d2";

  useEffect(() => {
    if (registrationPath === "invitation") {
      setPartnerTab(preferredInvitationTab);

      if (
        preferredInvitationTab === 0 &&
        localStorage.getItem("tournament_partner_tour_v2") !== "true"
      ) {
        const t = setTimeout(() => setGuideStep(0), 200);
        return () => clearTimeout(t);
      } else {
        setGuideStep(null);
      }
    }
  }, [registrationPath, preferredInvitationTab]);

  const guideSteps = [
    {
      tabId: "tour-tab-existing",
      title: isPadelEagle ? "👤 Current Padel Eagle Player" : "👤 Current SP Player",
      content:
        isPadelEagle
          ? "Your partner already plays Padel Eagle tournaments or has played before. Search their name and send a direct invitation — they'll get it in their dashboard."
          : "Your partner already plays in SPL or has played a tournament before. Search their name and send a direct invitation — they'll get it in their dashboard.",
    },
    {
      tabId: "tour-tab-new",
      title: "🔗 New Player",
      content:
        "Your partner doesn't have an account yet. Enter their name to get a shareable registration link. Once they sign up through it you're automatically paired as a team.",
    },
    {
      tabId: "tour-tab-board",
      title: "🔍 Partner Board",
      content:
        "Other players looking for a partner list themselves here. Browse and invite anyone whose level and style fits.",
    },
  ];

  const partnerTabMeta = [
    {
      label: isPadelEagle ? "Current Padel Eagle Player" : "Current SP Player",
      helper: isPadelEagle ? "Plays Padel Eagle tournaments or played before" : "Plays SPL or played before",
    },
    {
      label: "New Player",
      helper: "Needs a registration link",
    },
    {
      label: "Partner Board",
      helper: "Browse available partners",
    },
  ];

  const dismissGuide = () => {
    localStorage.setItem("tournament_partner_tour_v2", "true");
    setGuideStep(null);
  };

  const nextGuideStep = () => {
    if (guideStep !== null && guideStep < guideSteps.length - 1) {
      setGuideStep(guideStep + 1);
      setPartnerTab((guideStep + 1) as 0 | 1 | 2);
    } else {
      setPartnerTab(0);
      dismissGuide();
    }
  };

  // Invitation path state
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [teamName, setTeamName] = useState("");
  const [personalMessage, setPersonalMessage] = useState("");
  const [isNewPlayer, setIsNewPlayer] = useState(false);
  const [registrationLink, setRegistrationLink] = useState("");
  const [pendingInvitationId, setPendingInvitationId] = useState("");
  const [newPlayerName, setNewPlayerName] = useState("");
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showCopyFirstHint, setShowCopyFirstHint] = useState(false);

  // Partner board path state
  const [bio, setBio] = useState("");
  const [preferredLevelMin, setPreferredLevelMin] = useState<number>(
    Math.max(1, (currentPlayer.level || 5) - 2),
  );
  const [preferredLevelMax, setPreferredLevelMax] = useState<number>(
    Math.min(10, (currentPlayer.level || 5) + 2),
  );

  // Board players state
  const [boardPlayers, setBoardPlayers] = useState<any[]>([]);
  const [boardLoading, setBoardLoading] = useState(false);
  const [detailBoardPlayer, setDetailBoardPlayer] = useState<any | null>(null);
  const [selectedBoardPlayer, setSelectedBoardPlayer] = useState<any | null>(
    null,
  );
  const [boardPlayerPassword, setBoardPlayerPassword] = useState(""); // Password to unlock board player

  // Common state
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Voucher code state
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherStatus, setVoucherStatus] = useState<
    "idle" | "checking" | "valid" | "invalid"
  >("idle");
  const [voucherMessage, setVoucherMessage] = useState("");

  useEffect(() => {
    if (open) {
      fetchPlayers();
      fetchBoardPlayers();
      resetForm();
    }
  }, [open]);

  const fetchPlayers = async () => {
    try {
      const params = new URLSearchParams();
      params.append("activeOnly", "true");
      // Don't filter by division - show all players for "Existing Player" tab

      const response = await apiRequest(`/api/players?${params}`);
      const data = await response.json();
      // Filter out current player
      setPlayers(data.filter((p: Player) => p.id !== currentPlayer.id));
    } catch (err) {
      console.error("Failed to load players:", err);
    }
  };

  const fetchBoardPlayers = async () => {
    setBoardLoading(true);
    try {
      // Fetch players from the partner board for this tournament
      const response = await apiRequest(
        `/api/tournaments/${tournament.id}/partner-board`,
      );
      if (response.ok) {
        const data = await response.json();
        // Exclude the current player from the board so they can't select themselves
        setBoardPlayers(
          data.filter(
            (p: any) => p.playerId !== currentPlayer.id && p.isActive !== false,
          ),
        );
      }
    } catch (err) {
      console.error("Failed to load board players:", err);
    } finally {
      setBoardLoading(false);
    }
  };

  const handleLookingForPartner = () => {
    if (boardPlayers.length > 0) {
      setPreferredInvitationTab(2);
      setRegistrationPath("invitation");
      return;
    }

    setRegistrationPath("partner-board");
  };

  const resetForm = () => {
    setRegistrationPath(null);
    setPreferredInvitationTab(0);
    setPartnerTab(0);
    setSelectedPlayer(null);
    setRecipientEmail("");
    setRecipientName("");
    setTeamName("");
    setPersonalMessage("");
    setIsNewPlayer(false);
    setBio("");
    setError("");
    setSuccess(false);
    setVoucherCode("");
    setVoucherStatus("idle");
    setVoucherMessage("");
  };

  const handleApplyVoucher = async () => {
    if (!voucherCode.trim()) return;
    setVoucherStatus("checking");
    setVoucherMessage("");
    try {
      const response = await apiRequest("/api/discount-codes/validate", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${playerToken}`,
        },
        body: JSON.stringify({
          code: voucherCode.trim(),
          scope: "tournament",
          scopeTargetId: tournament.id,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.valid) {
        setVoucherStatus("valid");
        setVoucherMessage(`${data.label} off applied!`);
      } else {
        setVoucherStatus("invalid");
        setVoucherMessage(data.error || "Invalid voucher code");
      }
    } catch (err) {
      setVoucherStatus("invalid");
      setVoucherMessage("Failed to validate voucher code");
    }
  };

  const handleSendInvitation = async () => {
    // Validate based on which tab is active
    if (partnerTab === 0) {
      // Tab 0: Existing Player
      if (!selectedPlayer) {
        setError("Please select a player");
        return;
      }
    } else if (partnerTab === 1) {
      // Tab 1: New Player - validate name
      if (!newPlayerName.trim()) {
        setError("Please enter your partner's name");
        return;
      }
    } else if (partnerTab === 2) {
      // Tab 2: Partner Board selection
      if (!selectedBoardPlayer) {
        setError("Please select a player from the board");
        return;
      }
      if (selectedBoardPlayer.isLocked && !boardPlayerPassword) {
        setError("Please enter the password for this player");
        return;
      }
    }

    setLoading(true);
    setError("");

    try {
      // Tab 2: Selecting from Partner Board
      if (partnerTab === 2 && selectedBoardPlayer) {
        // Send invitation to the board player
        const invitationData = {
          tournamentId: tournament.id,
          recipientPlayerId: selectedBoardPlayer.playerId,
          teamName: teamName || undefined,
          personalMessage: `Hi! I'd like to partner with you for ${tournament.name}`,
          boardEntryPassword: selectedBoardPlayer.isLocked
            ? boardPlayerPassword
            : undefined,
          voucherCode: voucherStatus === "valid" ? voucherCode.trim() : undefined,
        };

        const invRes = await apiRequest("/api/tournament-invitations", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${playerToken}`,
          },
          body: JSON.stringify(invitationData),
        });

        if (!invRes.ok) {
          const errData = await invRes.json().catch(() => ({}));
          throw new Error(errData.error || `Failed to send invitation (${invRes.status})`);
        }

        setSuccess(true);
        setTimeout(() => {
          onClose();
          if (onSuccess) onSuccess();
        }, 2000);
        return;
      }

      // Tab 1: New Player - create pending invitation for new player
      if (partnerTab === 1) {
        const invitationData = {
          tournamentId: tournament.id,
          recipientName: newPlayerName.trim(),
          teamName: teamName || `Team ${currentPlayer.name.split(' ')[0]} & ${newPlayerName.split(' ')[0]}`,
          isNewPlayer: true,
          voucherCode: voucherStatus === "valid" ? voucherCode.trim() : undefined,
        };

        const response = await apiRequest("/api/tournament-invitations", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${playerToken}`,
          },
          body: JSON.stringify(invitationData),
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || `Failed to send invitation (${response.status})`);
        }

        const data = await response.json();
        setPendingInvitationId(data.id);

        // Generate link with invitation ID
        const baseUrl = window.location.origin;
        const params = new URLSearchParams({
          invitationId: data.id,
          tournamentId: tournament.id,
          partnerId: currentPlayer.id,
          partnerName: currentPlayer.name,
          recipientName: newPlayerName.trim(),
          orgSlug: currentOrg?.slug || "",
        });

        const link = `${baseUrl}/player-registration?${params.toString()}`;
        setRegistrationLink(link);
        setLinkCopied(false);
        setShowCopyFirstHint(false);
        setShowLinkDialog(true);

        setLoading(false);
        return;
      }

      // Tab 0: Existing Player - send invitation
      if (partnerTab === 0 && selectedPlayer) {
        const invitationData = {
          tournamentId: tournament.id,
          recipientPlayerId: selectedPlayer.id,
          recipientEmail: selectedPlayer.email || "",
          teamName: teamName || undefined,
          personalMessage: personalMessage || undefined,
          voucherCode: voucherStatus === "valid" ? voucherCode.trim() : undefined,
        };

        const response = await apiRequest("/api/tournament-invitations", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${playerToken}`,
          },
          body: JSON.stringify(invitationData),
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || `Failed to send invitation (${response.status})`);
        }
        await response.json();
        setSuccess(true);
        setTimeout(() => {
          onClose();
          if (onSuccess) onSuccess();
        }, 2000);
      }
    } catch (err: any) {
      setError(err.message || "Failed to send invitation");
      setLoading(false);
    }
  };

  const handleJoinPartnerBoard = async () => {
    if (!bio.trim()) {
      setError("Please tell us a bit about your playing style");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await apiRequest("/api/partner-board", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${playerToken}`,
        },
        body: JSON.stringify({
          tournamentId: tournament.id,
          bio: bio.trim(),
          preferredPartnerLevelMin: preferredLevelMin,
          preferredPartnerLevelMax: preferredLevelMax,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(
          errData.error || `Failed to join partner board (${response.status})`,
        );
      }

      await response.json().catch(() => ({}));

      setSuccess(true);
      setTimeout(() => {
        onClose();
        if (onSuccess) onSuccess();
      }, 2000);
    } catch (err: any) {
      setError(err.message || "Failed to join partner board");
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      resetForm();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={window.innerWidth < 600}
      sx={{
        '& .MuiDialog-paper': {
          m: { xs: 0, sm: 2 },
          maxHeight: { xs: '100%', sm: '90vh' },
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <span style={{ fontSize: "1.5rem" }}>🎾</span>
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Register for {tournament.name}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2 } }}>
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {registrationPath === "invitation"
              ? "Invitation sent successfully! Your partner will receive an email notification."
              : "You've joined the Partner Board! Other players can now invite you."}
          </Alert>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Tournament Info */}
            <Box sx={{ mb: 3, p: { xs: 1.5, sm: 2 }, bgcolor: "action.hover", borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                <strong>Tournament:</strong> {tournament.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                <strong>Date:</strong>{" "}
                {new Date(tournament.startDate).toLocaleDateString()}
              </Typography>
              {tournament.venue && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                  <strong>Venue:</strong> {tournament.venue}
                </Typography>
              )}
            </Box>

            {/* Path Selection */}
            {!registrationPath ? (
              <Box>
                {!isPadelEagle && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" gutterBottom sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                    Have a voucher code?
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                    <TextField
                      size="small"
                      placeholder="Enter code"
                      value={voucherCode}
                      onChange={(e) => {
                        setVoucherCode(e.target.value.toUpperCase());
                        setVoucherStatus("idle");
                        setVoucherMessage("");
                      }}
                      disabled={voucherStatus === "valid"}
                      sx={{ flexGrow: 1 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleApplyVoucher}
                      disabled={
                        !voucherCode.trim() ||
                        voucherStatus === "checking" ||
                        voucherStatus === "valid"
                      }
                      sx={{ height: 40 }}
                    >
                      {voucherStatus === "checking" ? (
                        <CircularProgress size={18} />
                      ) : voucherStatus === "valid" ? (
                        "Applied"
                      ) : (
                        "Apply"
                      )}
                    </Button>
                  </Box>
                  {voucherMessage && (
                    <Typography
                      variant="caption"
                      color={voucherStatus === "valid" ? "success.main" : "error"}
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {voucherStatus === "valid" ? "✓ " : ""}
                      {voucherMessage}
                    </Typography>
                  )}
                </Box>
                )}

                <Typography variant="body1" gutterBottom sx={{ mb: 2, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  How would you like to register?
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<PersonAddIcon />}
                    onClick={() => {
                      setPreferredInvitationTab(0);
                      setRegistrationPath("invitation");
                    }}
                    sx={{ 
                      py: { xs: 1.5, sm: 2 }, 
                      justifyContent: "flex-start",
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                    }}
                  >
                    <Box sx={{ textAlign: "left", ml: { xs: 0, sm: 1 } }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                        I have a partner
                      </Typography>
                      <Typography variant="body2" color="inherit" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, opacity: 0.75 }}>
                        Send an invitation to your partner
                      </Typography>
                    </Box>
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<SearchIcon />}
                    onClick={handleLookingForPartner}
                    disabled={boardLoading}
                    sx={{ 
                      py: { xs: 1.5, sm: 2 }, 
                      justifyContent: "flex-start",
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                    }}
                  >
                    <Box sx={{ textAlign: "left", ml: { xs: 0, sm: 1 } }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                        I'm looking for a partner
                      </Typography>
                      <Typography variant="body2" color="inherit" sx={{ opacity: 0.75 }}>
                        {boardLoading
                          ? "Checking Partner Board..."
                          : boardPlayers.length > 0
                            ? "Browse Partner Board players first and invite directly"
                            : "No board entries yet - join the Partner Board"}
                      </Typography>
                    </Box>
                  </Button>
                </Box>
              </Box>
            ) : registrationPath === "invitation" ? (
              // Invitation Form with Tabs
              <Box>
                <Button
                  size="small"
                  onClick={() => setRegistrationPath(null)}
                  sx={{ mb: 2 }}
                >
                  ← Back
                </Button>

                <Typography variant="subtitle1" gutterBottom>
                  Find Your Partner
                </Typography>

                <Tabs
                  value={partnerTab}
                  onChange={(_, value) => setPartnerTab(value)}
                  orientation={window.innerWidth < 600 ? "vertical" : "horizontal"}
                  variant={window.innerWidth < 600 ? "fullWidth" : "standard"}
                  sx={{
                    mb: 2,
                    p: 0.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: "action.hover",
                    "& .MuiTabs-indicator": {
                      display: "none",
                    },
                    "& .MuiTab-root": {
                      minHeight: { xs: 64, sm: 48 },
                      fontSize: { xs: "0.85rem", sm: "0.875rem" },
                      textTransform: "none",
                      alignItems: "flex-start",
                      px: { xs: 1.25, sm: 1.5 },
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      mr: { xs: 0, sm: 0.5 },
                      mb: { xs: 0.5, sm: 0 },
                      bgcolor: "background.paper",
                      opacity: 0.9,
                      transition: "all 0.2s ease",
                    },
                    "& .MuiTab-root.Mui-selected": {
                      borderColor: "primary.main",
                      boxShadow: 2,
                      bgcolor: "rgba(25, 118, 210, 0.12)",
                      opacity: 1,
                    },
                    "& .MuiTab-root:last-of-type": {
                      mr: 0,
                    },
                  }}
                >
                  <Tab
                    id="tour-tab-existing"
                    label={
                      <Box sx={{ textAlign: "left" }}>
                        <Typography sx={{ fontSize: { xs: "0.84rem", sm: "0.875rem" }, fontWeight: 700, lineHeight: 1.2 }}>
                          {partnerTabMeta[0].label}
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", lineHeight: 1.2, mt: 0.3 }}>
                          {partnerTabMeta[0].helper}
                        </Typography>
                      </Box>
                    }
                  />
                  <Tab
                    id="tour-tab-new"
                    label={
                      <Box sx={{ textAlign: "left" }}>
                        <Typography sx={{ fontSize: { xs: "0.84rem", sm: "0.875rem" }, fontWeight: 700, lineHeight: 1.2 }}>
                          {partnerTabMeta[1].label}
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", lineHeight: 1.2, mt: 0.3 }}>
                          {partnerTabMeta[1].helper}
                        </Typography>
                      </Box>
                    }
                  />
                  <Tab
                    id="tour-tab-board"
                    label={
                      <Box sx={{ textAlign: "left" }}>
                        <Typography sx={{ fontSize: { xs: "0.84rem", sm: "0.875rem" }, fontWeight: 700, lineHeight: 1.2 }}>
                          {partnerTabMeta[2].label}
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", lineHeight: 1.2, mt: 0.3 }}>
                          {partnerTabMeta[2].helper}
                        </Typography>
                      </Box>
                    }
                  />
                </Tabs>

                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                  Selected: {partnerTabMeta[partnerTab].label} - {partnerTabMeta[partnerTab].helper}
                </Typography>

                {/* Tab 0: Existing Player */}
                {partnerTab === 0 && (
                  <Box>
                    <Alert severity="info" icon={<SendIcon />} sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Send an invitation</strong> to a current {isPadelEagle ? "Padel Eagle" : "SP"} player (already in {isPadelEagle ? "Padel Eagle" : "SPL"} or played before). They will receive a notification and must accept in their dashboard.
                      </Typography>
                    </Alert>

                    <Autocomplete
                      options={players}
                      getOptionLabel={(player) =>
                        `${player.name} ${player.level ? `(Level ${player.level})` : ""} ${player.division ? `- ${player.division}` : ""}`
                      }
                      value={selectedPlayer}
                      onChange={(_, newValue) => setSelectedPlayer(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Partner *"
                          placeholder="Search players..."
                        />
                      )}
                      sx={{ mb: 2 }}
                    />

                    <TextField
                      fullWidth
                      label="Team Name (Optional)"
                      value={teamName}
                      onChange={(e) => setTeamName(e.target.value)}
                      placeholder={`Team ${currentPlayer.name.split(" ")[0]} & ${selectedPlayer?.name.split(" ")[0] || "Partner"}`}
                      sx={{ mb: 2 }}
                    />

                    <TextField
                      fullWidth
                      label="Personal Message (Optional)"
                      multiline
                      rows={3}
                      value={personalMessage}
                      onChange={(e) => setPersonalMessage(e.target.value)}
                      placeholder="Hey! Want to team up for this tournament?"
                      sx={{ mb: 2 }}
                    />
                  </Box>
                )}

                {/* Tab 1: New Player */}
                {partnerTab === 1 && (
                  <Box>
                    {!registrationLink ? (
                      <>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>Create a registration invitation</strong> for your new partner. Enter their details and generate a link they can use to register.
                          </Typography>
                        </Alert>

                        <TextField
                          fullWidth
                          label="Partner's Name *"
                          value={newPlayerName}
                          onChange={(e) => setNewPlayerName(e.target.value)}
                          placeholder="e.g., John Smith"
                          sx={{ mb: 2 }}
                        />

                        <TextField
                          fullWidth
                          label="Team Name (Optional)"
                          value={teamName}
                          onChange={(e) => setTeamName(e.target.value)}
                          placeholder={`Team ${currentPlayer.name.split(" ")[0]} & ${newPlayerName.split(" ")[0] || "Partner"}`}
                          sx={{ mb: 2 }}
                        />
                      </>
                    ) : null}
                  </Box>
                )}

                {/* Tab 2: Partner Board */}
                {partnerTab === 2 && (
                  <Box>
                    <Alert severity="info" icon={<SearchIcon />} sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Browse players</strong> who are looking for partners. Select one and send them an invitation to team up!
                      </Typography>
                    </Alert>

                    {boardPlayers.length === 0 ? (
                      <Alert severity="info">
                        No players currently on the partner board for this
                        tournament
                      </Alert>
                    ) : (
                      <>
                        {/* Mobile hint */}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: { xs: "block", sm: "none" }, mb: 1.5, textAlign: "center" }}
                        >
                          👆 Tap a card to see more info — tap again to select
                        </Typography>

                        <Grid container spacing={{ xs: 1.5, sm: 3 }} sx={{ mb: 3 }}>
                          {boardPlayers.map((player) => {
                            const isSelected = selectedBoardPlayer?.id === player.id;
                            return (
                              <Grid item xs={6} sm={6} md={4} key={player.id}>
                                <Card
                                  elevation={isSelected ? 8 : 3}
                                  sx={{
                                    borderRadius: 3,
                                    overflow: "hidden",
                                    boxShadow: isSelected ? 6 : 2,
                                    border: isSelected ? "2px solid" : "1px solid",
                                    borderColor: isSelected ? "primary.main" : "divider",
                                    bgcolor: "background.paper",
                                    position: "relative",
                                    transition: "all 0.2s",
                                    cursor: "pointer",
                                    "&:hover": { boxShadow: 8, transform: "scale(1.03)" },
                                  }}
                                >
                                  <CardActionArea
                                    onClick={() => {
                                      // On mobile open detail; on desktop select directly
                                      setDetailBoardPlayer(player);
                                    }}
                                    sx={{ height: "100%" }}
                                  >
                                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: { xs: 1.5, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
                                      <Avatar
                                        src={player.profilePictureUrl}
                                        alt={player.playerName}
                                        sx={{
                                          width: { xs: 56, sm: 96 },
                                          height: { xs: 56, sm: 96 },
                                          mb: 1,
                                          border: "3px solid",
                                          borderColor: isSelected ? "primary.main" : "divider",
                                          boxShadow: 2,
                                        }}
                                      >
                                        {player.playerName?.charAt(0) || "?"}
                                      </Avatar>
                                      {isSelected && (
                                        <Chip label="Selected ✓" size="small" color="primary" sx={{ mb: 0.5, fontSize: "0.7rem" }} />
                                      )}
                                      <Typography
                                        align="center"
                                        sx={{ fontWeight: 700, color: "primary.dark", fontSize: { xs: "0.8rem", sm: "1.25rem" }, lineHeight: 1.2, px: 0.5 }}
                                      >
                                        {player.playerName}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Level <b>{player.level || "N/A"}</b>
                                      </Typography>
                                      {player.isLocked && (
                                        <LockIcon sx={{ position: "absolute", top: 8, right: 8, color: "error.main", fontSize: { xs: 18, sm: 24 } }} />
                                      )}
                                    </Box>
                                    {/* Bio and level preference — hidden on mobile */}
                                    <CardContent sx={{ px: { xs: 1, sm: 2 }, pb: { xs: 1, sm: 2 }, display: { xs: "none", sm: "block" } }}>
                                      {player.bio && (
                                        <Typography
                                          variant="body2" color="text.secondary"
                                          sx={{ mb: 2, fontSize: "0.95rem", fontStyle: "italic",
                                            display: "-webkit-box", WebkitLineClamp: 3,
                                            WebkitBoxOrient: "vertical", overflow: "hidden", minHeight: "3.75em" }}
                                        >
                                          "{player.bio}"
                                        </Typography>
                                      )}
                                      {(player.preferredLevelMin || player.preferredLevelMax) && (
                                        <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                                          <Typography variant="caption" color="text.secondary">Looking for:</Typography>
                                          <Chip
                                            label={`Lvl ${player.preferredLevelMin || "?"}–${player.preferredLevelMax || "?"}`}
                                            size="small" color="primary" variant="outlined"
                                            sx={{ fontWeight: 600, fontSize: "0.8rem" }}
                                          />
                                        </Box>
                                      )}
                                    </CardContent>
                                  </CardActionArea>
                                </Card>
                              </Grid>
                            );
                          })}
                        </Grid>

                        {/* Player detail dialog (tap on mobile, also works on desktop) */}
                        <Dialog
                          open={!!detailBoardPlayer}
                          onClose={() => setDetailBoardPlayer(null)}
                          maxWidth="xs"
                          fullWidth
                          PaperProps={{ sx: { mx: { xs: 1, sm: 2 }, borderRadius: 3 } }}
                        >
                          {detailBoardPlayer && (
                            <>
                              <DialogTitle sx={{ pb: 1 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                  <Typography variant="h6">Player Profile</Typography>
                                  <IconButton size="small" onClick={() => setDetailBoardPlayer(null)}>
                                    <CloseIcon />
                                  </IconButton>
                                </Box>
                              </DialogTitle>
                              <DialogContent>
                                <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
                                  <Avatar
                                    src={detailBoardPlayer.profilePictureUrl}
                                    alt={detailBoardPlayer.playerName}
                                    sx={{ width: 80, height: 80, mb: 1.5, border: "3px solid", borderColor: "divider" }}
                                  >
                                    {detailBoardPlayer.playerName?.charAt(0) || "?"}
                                  </Avatar>
                                  <Typography variant="h6" fontWeight={700} align="center">
                                    {detailBoardPlayer.playerName}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Level <b>{detailBoardPlayer.level || "N/A"}</b>
                                  </Typography>
                                  {detailBoardPlayer.isLocked && (
                                    <Chip label="🔒 Locked" size="small" color="error" variant="outlined" sx={{ mt: 1 }} />
                                  )}
                                </Box>
                                {detailBoardPlayer.bio && (
                                  <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ mb: 2 }}>
                                    "{detailBoardPlayer.bio}"
                                  </Typography>
                                )}
                                {(detailBoardPlayer.preferredLevelMin || detailBoardPlayer.preferredLevelMax) && (
                                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <Typography variant="caption" color="text.secondary">Looking for partner:</Typography>
                                    <Chip
                                      label={`Level ${detailBoardPlayer.preferredLevelMin || "?"}–${detailBoardPlayer.preferredLevelMax || "?"}`}
                                      size="small" color="primary" variant="outlined"
                                    />
                                  </Box>
                                )}
                              </DialogContent>
                              <DialogActions sx={{ px: 2, pb: 2 }}>
                                <Button onClick={() => setDetailBoardPlayer(null)}>Cancel</Button>
                                <Button
                                  variant="contained"
                                  onClick={() => {
                                    setSelectedBoardPlayer(detailBoardPlayer);
                                    setBoardPlayerPassword("");
                                    setDetailBoardPlayer(null);
                                  }}
                                >
                                  Select as Partner
                                </Button>
                              </DialogActions>
                            </>
                          )}
                        </Dialog>

                        {selectedBoardPlayer?.isLocked && (
                          <PasswordTextField
                            fullWidth label="Partner Password *"
                            value={boardPlayerPassword}
                            onChange={(e) => setBoardPlayerPassword(e.target.value)}
                            sx={{ mb: 2 }}
                            helperText="This player is locked. Enter their password to select them."
                          />
                        )}
                        {selectedBoardPlayer && (
                          <TextField
                            fullWidth label="Team Name (Optional)"
                            value={teamName}
                            onChange={(e) => setTeamName(e.target.value)}
                            placeholder={`Team ${currentPlayer.name.split(" ")[0]} & ${selectedBoardPlayer?.playerName.split(" ")[0] || "Partner"}`}
                            sx={{ mb: 2 }}
                          />
                        )}

                        <Button
                          fullWidth
                          variant="contained"
                          color="warning"
                          size="large"
                          startIcon={<PersonAddIcon />}
                          onClick={() => setRegistrationPath("partner-board")}
                          sx={{
                            mt: 2,
                            py: 1.25,
                            fontWeight: 800,
                            fontSize: { xs: "0.9rem", sm: "0.95rem" },
                            textTransform: "none",
                            border: "2px solid",
                            borderColor: "warning.dark",
                            boxShadow: "0 6px 16px rgba(245, 124, 0, 0.35)",
                            "&:hover": {
                              boxShadow: "0 8px 20px rgba(245, 124, 0, 0.45)",
                            },
                          }}
                        >
                          No player suits your requirements? Put yourself on the Partner Board
                        </Button>
                      </>
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              // Partner Board Form
              <Box>
                <Button
                  size="small"
                  onClick={() => setRegistrationPath(null)}
                  sx={{ mb: 2 }}
                >
                  ← Back
                </Button>

                <Typography variant="subtitle1" gutterBottom>
                  Join the Partner Board
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Tell other players about yourself and your preferences. They
                  can send you an invitation if they'd like to team up!
                </Typography>

                <TextField
                  fullWidth
                  label="About Your Playing Style *"
                  multiline
                  rows={3}
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="e.g., Aggressive player, prefer net play, looking for competitive partner"
                  sx={{ mb: 2 }}
                  inputProps={{ maxLength: 200 }}
                  helperText={`${bio.length}/200 characters`}
                />

                <Typography variant="body2" gutterBottom>
                  Preferred Partner Level
                </Typography>
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Min Level</InputLabel>
                    <Select
                      value={preferredLevelMin}
                      onChange={(e) =>
                        setPreferredLevelMin(Number(e.target.value))
                      }
                      label="Min Level"
                    >
                      {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map((level) => (
                        <MenuItem key={level} value={level}>
                          Level {level}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Max Level</InputLabel>
                    <Select
                      value={preferredLevelMax}
                      onChange={(e) =>
                        setPreferredLevelMax(Number(e.target.value))
                      }
                      label="Max Level"
                    >
                      {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map((level) => (
                        <MenuItem key={level} value={level}>
                          Level {level}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Alert severity="info">
                  💡 Your profile will be visible on the Partner Board. Other
                  players can browse and send you invitations.
                </Alert>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        {registrationPath && !success && !registrationLink && (
          <Button
            variant="contained"
            onClick={
              registrationPath === "invitation"
                ? handleSendInvitation
                : handleJoinPartnerBoard
            }
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={20} />
              ) : registrationPath === "invitation" && partnerTab === 1 ? (
                <LinkIcon />
              ) : registrationPath === "invitation" ? (
                <SendIcon />
              ) : (
                <PersonAddIcon />
              )
            }
          >
            {loading
              ? "Processing..."
              : registrationPath === "invitation" && partnerTab === 1
                ? "Confirm Registration"
                : registrationPath === "invitation"
                ? "Send Invitation"
                : "Join Partner Board"}
          </Button>
        )}
      </DialogActions>

      {/* Step-by-step guide tooltip */}
      {guideStep !== null && guideStep < guideSteps.length && (() => {
        const step = guideSteps[guideStep];
        const anchor = document.getElementById(step.tabId);
        if (!anchor) return null;
        return (
          <Popper
            open
            anchorEl={anchor}
            placement="bottom"
            transition
            style={{ zIndex: 9999 }}
            modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={200}>
                <Paper
                  elevation={6}
                  sx={{
                    p: 2,
                    maxWidth: 260,
                    borderRadius: 2,
                    border: `2px solid ${primaryColor}`,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {step.content}
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="caption" color="text.secondary">
                      {guideStep + 1} / {guideSteps.length}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button size="small" onClick={dismissGuide} sx={{ fontSize: "0.75rem" }}>
                        Skip
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={nextGuideStep}
                        sx={{ fontSize: "0.75rem", bgcolor: primaryColor, "&:hover": { bgcolor: primaryColor, opacity: 0.9 } }}
                      >
                        {guideStep === guideSteps.length - 1 ? "Got it!" : "Next →"}
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Fade>
            )}
          </Popper>
        );
      })()}

      {/* Registration Link Dialog */}
      <Dialog
        open={showLinkDialog}
        onClose={() => {
          // The link is only shown here, so don't let it be dismissed before it was copied
          if (!linkCopied) {
            setShowCopyFirstHint(true);
            return;
          }
          setShowLinkDialog(false);
          onClose();
          if (onSuccess) onSuccess();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleOutlineIcon color="success" />
            Registration Link Created
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Share this link with <strong>{newPlayerName}</strong>. Your team will be registered once they complete their account.
          </Typography>
          <TextField
            fullWidth
            value={registrationLink}
            InputProps={{
              readOnly: true,
            }}
            sx={{ mb: 2 }}
          />
          {showCopyFirstHint && !linkCopied && (
            <Alert severity="warning">
              Please click "Copy Link" first, so you can send it to {newPlayerName}. The link won't be shown again.
            </Alert>
          )}
          {linkCopied && (
            <Alert severity="success">Link copied. Send it to {newPlayerName}.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(registrationLink);
              } catch {
                // Clipboard blocked: the link is selectable in the field above, so don't trap the player
              }
              setLinkCopied(true);
              setShowCopyFirstHint(false);
            }}
            startIcon={<ContentCopyIcon />}
          >
            {linkCopied ? "Copied" : "Copy Link"}
          </Button>
          {/* Looks disabled until the link was copied, but stays clickable to explain why */}
          <Button
            variant="contained"
            aria-disabled={!linkCopied}
            sx={!linkCopied ? { opacity: 0.45, cursor: "not-allowed" } : undefined}
            onClick={() => {
              if (!linkCopied) {
                setShowCopyFirstHint(true);
                return;
              }
              setShowLinkDialog(false);
              onClose();
              if (onSuccess) onSuccess();
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
